/**
 * The onboarding chat's conversation state: message history, the streamed
 * reply, and the onboarding result accumulated across turns.
 *
 * Invariants:
 * - Post-await code reads messagesRef/resultRef, never the state values,
 *   which can be a render behind. commitMessages writes messagesRef before
 *   React re-renders, so a fast follow-up submit sees the committed history.
 * - messagesRef and resultRef are re-synced from state on every render, so
 *   resetConversationState and restoreConversationState leave them to that
 *   sync instead of writing them.
 * - The accumulator never resets between turns (useObject clears `object` on
 *   each submit, and the model need not re-emit captured fields); only
 *   resetConversationState and restoreConversationState replace it.
 */
"use client";

import { useCallback, useRef, useState } from "react";
import {
  mergePartialResults,
  type OnboardingResult,
  type PartialOnboardingResult,
} from "@/lib/onboarding/schema";
import type { OnboardingChatPayload } from "@/lib/onboarding/session";

type ChatMessage = { role: "user" | "assistant"; content: string };

export function useOnboardingConversation() {
  const [result, setResult] = useState<OnboardingResult | null>(null);
  const resultRef = useRef<OnboardingResult | null>(null);
  resultRef.current = result;

  // Message history (managed locally, sent with each request)
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Current streaming message from assistant. The ref survives into onFinish,
  // where useObject's `object` may already be undefined.
  const [streamingMessage, setStreamingMessage] = useState("");
  const streamingMessageRef = useRef("");

  // Read by Skip, Build, and sessionStorage persistence.
  const accumulatedResultRef = useRef<OnboardingResult | null>(null);
  // True once a streamed partial actually included rubricType. Distinguishes a
  // real venture choice from the schema default so the conversion backup can
  // keep it (see saveOnboardingSessionBackup).
  const rubricTypeDirtyRef = useRef(false);
  // Reactive mirror of accumulatedResultRef — unlike the ref, this triggers
  // effect re-runs so completion detection and sessionStorage saves react to
  // accumulated data even after useObject resets between turns.
  const [stableResult, setStableResult] = useState<OnboardingResult | null>(null);

  const commitMessages = useCallback((next: ChatMessage[]) => {
    messagesRef.current = next;
    setMessages(next);
  }, []);

  // Returns the merged value so the caller can optionally pass it to
  // setStableResult.
  const flushToAccumulator = useCallback(
    (partial: PartialOnboardingResult | null | undefined): OnboardingResult | null => {
      if (!partial) return accumulatedResultRef.current;
      if (partial.rubricType === "bootstrap") {
        rubricTypeDirtyRef.current = true;
      } else if (
        Object.hasOwn(partial, "rubricType") &&
        partial.rubricType === "venture" &&
        !("raisingIntent" in partial && "launchStatus" in partial && "status" in partial)
      ) {
        // Streamed partials can carry an explicit venture choice. A full-schema
        // object always defaulted rubricType to venture, so that shape must not
        // count as a founder choice.
        rubricTypeDirtyRef.current = true;
      }
      const merged = mergePartialResults(accumulatedResultRef.current, partial);
      accumulatedResultRef.current = merged;
      return merged;
    },
    [],
  );

  const resetConversationState = useCallback(() => {
    setMessages([]);
    setResult(null);
    accumulatedResultRef.current = null;
    rubricTypeDirtyRef.current = false;
    setStableResult(null);
    setStreamingMessage("");
    streamingMessageRef.current = "";
  }, []);

  // `rubricTypeDirty` is the caller's verdict on the saved result (it reads
  // the conversion backup); it only ever sets the flag, never clears it.
  const restoreConversationState = useCallback(
    (saved: OnboardingChatPayload, opts: { rubricTypeDirty: boolean }) => {
      setMessages(saved.messages);
      if (!saved.result) return;
      setResult(saved.result);
      accumulatedResultRef.current = saved.result;
      setStableResult(saved.result);
      if (opts.rubricTypeDirty) rubricTypeDirtyRef.current = true;
    },
    [],
  );

  return {
    messages,
    messagesRef,
    commitMessages,
    result,
    setResult,
    resultRef,
    stableResult,
    setStableResult,
    streamingMessage,
    setStreamingMessage,
    streamingMessageRef,
    accumulatedResultRef,
    rubricTypeDirtyRef,
    flushToAccumulator,
    resetConversationState,
    restoreConversationState,
  };
}
